<template>
  <div v-if="type === 'normal'" :id="id" :class="['filter mt-3 p-3', {closed: isClosed}]">
    <div class="filter-label" @click="toggleClass">
      <svg class="filter-label-svg" style="enable-background:new 0 0 133.9 38;" version="1.1"
           viewBox="0 0 133.9 38" x="0px" xml:space="preserve" xmlns="http://www.w3.org/2000/svg"
           xmlns:xlink="http://www.w3.org/1999/xlink" y="0px"><g id="Pfad_444_1_" transform="translate(-11)"><path class="st0" d="M11,38V0h133.9v19.2L123.6,38H11z"/></g>
        <g id="Pfad_444" transform="translate(-11)"><path class="st1" d="M12.5,36.4V0h131.1v18.5l-20.3,17.9H12.5z"/></g>
            </svg>
      <div class="text">
        FILTER
        <icon type="filter"/>
      </div>
    </div>
    <div v-if="!fieldSelectorList" class="broom float-right" @click="clearFilter">
      <div class="hover-text"><span>{{ $t('clearFilter') }}</span></div>
      <icon type="broom"/>
    </div>
    <h3>{{ $t('filtering') }}</h3>
    <text-filter
        v-if="textFiltering"
        id="contentFilterText"
        :clear="forceClearKey"
        :field-name="textFilteringFieldName"
        class="mt-4"
        @filter="doFilter"
    />
    <org-filter
        v-if="orgFiltering"
        id="contentFilterOrgs"
        :clear="forceClearKey"
        :limit-to-organization-ids="limitToOrganizationIds"
        :type="orgFilterLocal ? 'local' : 'global'"
        :visibility-selection="[1, 2]"
        class="mt-3"
        @filter="doFilter"
        @listOpened="closeAllNonOrgFilters"
        @setOrgs="setOrgs"
    />
    <team-filter
        v-if="teamFiltering"
        id="contentFilterTeams"
        ref="teamFilter"
        :clear="forceClearKey"
        :collapsible="true"
        :limit-to-organization-ids="orgs"
        class="mt-2"
        @filter="doFilter"
        @listOpened="$refs.tagFilter ? $refs.tagFilter.closeItemList() : null"
    />
    <type-filter
        v-if="stateFilterItems"
        id="contentFilterState"
        :collapsible="false"
        :icons-only="false"
        :initial-type-filter="initialStateFilter"
        :type-filter-items="stateFilterItems"
        class="mt-3"
        filter-type="state"
        @filter="doFilter"
    />
    <type-filter
        v-if="typeFilterItems"
        id="contentFilterType"
        :clear="forceClearKey"
        :collapsible="false"
        :initial-type-filter="initialTypeFilter"
        :type-filter-items="typeFilterItems"
        class="mt-2"
        @filter="doFilter"
    />
    <media-category-filter
        v-if="categoryFilterItems"
        :id="id + 'contentFilterTypeCat'"
        :clear="forceClearKey"
        :collapsible="true"
        :filter-type="'mediaCategory'"
        :pre-selected-values="categoryFilterPreSelectedValues"
        :type-exclude-values="categoryFilterExcludeValues"
        :type-filter-items="categoryFilterItems"
        :type-filter-values="categoryFilterValues"
        @filter="doFilter"
    />
    <tag-filter
        v-if="tagFiltering"
        id="contentFilterTags"
        ref="tagFilter"
        :clear="forceClearKey"
        :collapsible="true"
        :limit-to-organization-ids="orgs"
        class="mt-2"
        @filter="doFilter"
    />
    <field-selector
        v-if="fieldSelectorList"
        id="contentFilterFieldSelector"
        :clear="forceClearKey"
        :content-id="id"
        :field-list="fieldSelectorList"
        :field-push-selected-item="fieldPushSelectedItem"
        @filter="doFilter"
        @getMinMax="getMinMax"
        @registerComponent="registerComponent"
        @unregisterComponent="unregisterComponent"
    />
    <visibility-filter
        v-if="visibilityFiltering"
        id="contentFilterVisibility"
        :clear="forceClearKey"
        :collapsible="true"
        @filter="doFilter"
        @registerComponent="registerComponent"
        @unregisterComponent="unregisterComponent"
    />
    <slot/>
  </div>
  <div v-else>
    <type-filter
        v-if="typeFilterItems"
        id="contentFilterType"
        :clear="forceClearKey"
        :collapsible="true"
        :initial-type-filter="initialTypeFilter"
        :type-filter-items="typeFilterItems"
        class="hidden"
        @filter="doFilter"
    />
    <type-filter
        v-if="stateFilterItems"
        id="contentFilterState"
        :collapsible="true"
        :icons-only="false"
        :initial-type-filter="initialStateFilter"
        :type-filter-items="stateFilterItems"
        class="hidden"
        filter-type="state"
        @filter="doFilter"
    />
    <media-category-filter
        v-if="categoryFilterItems"
        :id="id + 'contentFilterTypeCat'"
        :clear="forceClearKey"
        :collapsible="true"
        :filter-type="'mediaCategory'"
        :pre-selected-values="categoryFilterPreSelectedValues"
        :type-exclude-values="categoryFilterExcludeValues"
        :type-filter-items="categoryFilterItems"
        :type-filter-values="categoryFilterValues"
        class="hidden"
        @filter="doFilter"
    />
    <org-filter
        v-if="orgFiltering"
        id="contentFilterOrgs"
        :clear="forceClearKey"
        :limit-to-organization-ids="limitToOrganizationIds"
        :type="orgFilterLocal ? 'local' : 'global'"
        :visibility-selection="[1, 2]"
        class="hidden"
        @filter="doFilter"
    />
    <text-filter
        v-if="textFiltering"
        id="contentFilterText"
        :value="initialTextFilter"
        :store-previous-values="storePreviousValues"
        :class="[displayInline ? 'd-inline-block mr-2' : '']"
        @filter="doFilter"
    />
    <tag-filter
        v-if="tagFiltering"
        id="contentFilterTags"
        :class="[displayInline ? 'd-inline-block pb-0 pt-0' : '']"
        :clear="forceClearKey"
        :collapsible="true"
        :limit-to-organization-ids="orgs"
        :initial-tags="initialTagFilter"
        :store-previous-values="storePreviousValues"
        class="mt-2"
        @filter="doFilter"
    />
  </div>
</template>

<script>
/*
* To use this component the parent needs to implement a filter-method
* add v-on:filter="filter"
* in the filter method dispatch the corresponding loading method and pass forward the arguments
* */

import Icon from "../../Icon";
import TypeFilter from "./TypeFilter";
import TextFilter from "./TextFilter";
import TagFilter from "./TagFilter";
import TeamFilter from "./TeamFilter";
import OrgFilter from "./OrgFilter";
import FieldSelector from "./dataset/FieldSelector";
import MediaCategoryFilter from "./MediaCategoryFilter";
import VisibilityFilter from "@/components/widgets/filter/VisibilityFilter.vue";

export default {
  name: 'ContentFilter',
  components: {
    Icon,
    TextFilter,
    TypeFilter,
    TagFilter,
    OrgFilter,
    FieldSelector,
    MediaCategoryFilter,
    TeamFilter,
    VisibilityFilter
  },
  props: {
    id: {type: String, required: true,},
    type: {type: String, default: 'normal',},
    storeName: {type: String, required: true},
    typeFilterItems: {type: Array, default: null},
    initialTypeFilter: {
      type: Array, default: () => {
      }
    },
    categoryFilterValues: {type: Array, default: null},
    categoryFilterItems: {type: Array, default: null},
    /**
     * @param categoryFilterExcludeValues if you wish to exclude some items from the filtered result
     * */
    categoryFilterExcludeValues: {type: Array, default: null},
    categoryFilterPreSelectedValues: {type: Array, default: null},
    tagFiltering: {type: Boolean, default: true},
    teamFiltering: {type: Boolean, default: false},
    orgFiltering: {type: Boolean, default: true},
    orgFilterLocal: {type: Boolean, default: false},
    textFiltering: {type: Boolean, default: true},
    visibilityFiltering: {type: Boolean, default: false},
    textFilteringFieldName: {type: String, default: null},
    limitToOrganizationIds: {type: Array, default: null},
    fieldSelectorList: {type: Array, default: null},
    fieldPushSelectedItem: {type: Object, default: null},

    /***
     * The items to filter by state
     * e.g. ['a','h','i','d']
     * */
    stateFilterItems: {type: Array, default: null},
    initialStateFilter: {type: Array, default: () => {}},
    initialTagFilter: {type: Array, default: () => {}},
    initialTextFilter: {type: String, default: ''},
    /**
     * only for mini filter: whether or not to display things in one row
     * */
    displayInline: {type: Boolean, default: false},
    storePreviousValues: {type: Boolean, default: true},
  },
  data() {
    return {
      isClosed: this.$store.getters.getFilterStatus,
      emitable: false,
      isClearing: false,
      numberOfComponents: 0,
      numberOfInitializedComponentQueries: 0,
      initialFilter: {},
      forceClearKey: 0,
      orgs: []
    }
  },
  watch: {
    numberOfInitializedComponentQueries: function (val) {
      if (val === this.numberOfComponents) {
        this.emitable = true;
        this.doFilter(this.initialFilter, !this.isClearing, 'self');
        this.isClearing = false;
      }
    }
  },
  beforeMount() {
    this.isClosed = this.$store.getters.getFilterStatus;
    if (this.type === 'normal') {
      if (this.textFiltering) {
        this.numberOfComponents++;
      }
      if (this.typeFilterItems) {
        this.numberOfComponents++;
      }
      if (this.categoryFilterItems) {
        this.numberOfComponents++;
      }
      if (this.tagFiltering) {
        this.numberOfComponents++;
      }
      if (this.teamFiltering) {
        this.numberOfComponents++;
      }
      if (this.orgFiltering) {
        this.numberOfComponents++;
      }
      if (this.fieldSelectorList) {
        this.numberOfComponents++;
      }
      if (this.stateFilterItems) {
        this.numberOfComponents++;
      }
      if (this.visibilityFiltering) {
        this.numberOfComponents++;
      }
    } else {
      this.numberOfComponents = 1;
      if (this.tagFiltering) {
        this.numberOfComponents++;
      }
      if (this.orgFiltering) {
        this.numberOfComponents++;
      }
      if (this.categoryFilterItems) {
        this.numberOfComponents++;
      }
      if (this.typeFilterItems) {
        this.numberOfComponents++;
      }
      if (this.stateFilterItems) {
        this.numberOfComponents++;
      }
    }
    if(this.limitToOrganizationIds && this.limitToOrganizationIds.length > 0) {
      this.setOrgs(this.limitToOrganizationIds.map(id => {return {id: id}}));
    }
  },
  methods: {
    closeAllNonOrgFilters() {
      if (this.$refs.tagFilter) {
        this.$refs.tagFilter.closeItemList();
      }
      if (this.$refs.teamFilter) {
        this.$refs.teamFilter.closeItemList();
      }
    },
    reInitializeEmitFilter: function () {
      this.$emit('filter', {}, true);
    },
    clearFilter: function () {
      this.numberOfInitializedComponentQueries = 0;
      this.emitable = false;
      this.initialFilter = {};
      this.isClearing = true;
      this.forceClearKey++;
    },
    getMinMax: function (fieldName) {
      this.$emit('getMinMax', fieldName);
    },
    registerComponent(number = null) {
      this.numberOfComponents = number ? this.numberOfComponents + number : this.numberOfComponents++;
    },
    unregisterComponent() {
      this.numberOfComponents -= 1;
    },
    toggleClass() {
      this.$store.dispatch('setFilter', !this.isClosed);
      this.isClosed = !this.isClosed;
    },
    setOrgs(orgs) {
      this.orgs = orgs && orgs.length ? orgs.map(item => {
        return item.id
      }) : [];
    },
    doFilter(params, initial = false, componentName) {
      this.$log.warn(componentName);
      if (initial) {
        this.numberOfInitializedComponentQueries++;
        if (params.add) {
          let add = params.add;
          let key = Object.keys(add)[0];
          if (!this.initialFilter.add) {
            this.initialFilter.add = {};
          }
          if (!this.initialFilter.add[key]) {
            this.initialFilter.add[key] = {};
          }
          let paramKey = Object.keys(add[key])[0];
          this.initialFilter.add[key][paramKey] = params.add[key][paramKey];
        }
        if (params.remove) {
          let remove = params.remove;
          let key = Object.keys(remove)[0];
          if (!this.initialFilter.remove) {
            this.initialFilter.remove = {};
          }
          if (!this.initialFilter.remove[key]) {
            this.initialFilter.remove[key] = {};
          }
          let paramKey = Object.keys(remove[key])[0];
          this.initialFilter.remove[key][paramKey] = params.remove[key][paramKey];
        }
        if (params.addObject) {
          let addObject = params.addObject;
          let key = Object.keys(addObject)[0];
          if (!this.initialFilter.addObject) {
            this.initialFilter.addObject = {};
          }
          if (!this.initialFilter.addObject[key]) {
            this.initialFilter.addObject[key] = {};
          }
          let paramKey = Object.keys(addObject[key])[0];
          this.initialFilter.addObject[key][paramKey] = params.addObject[key][paramKey];
        }
        if (this.emitable) {
          this.$emit('filter', params, true);
        }
      } else {
        this.$emit('filter', params, false);
      }
    },
  },
}
</script>
<style lang="scss">
$label-width: 38px; //rotated,that's why height = width
$filter-width: 300px;
$filter-inner-border-color: #444;

.filter-checkbox {
  margin-right: 5px;
}

.filter-label {
  display: inline-block;
  padding: 0.8em;
  position: absolute;
  z-index: 3;
  -webkit-transform: rotate(-90deg);
  transform: rotate(-90deg);
  transform-origin: top right;
  right: 0;
  top: -1px;
  height: $label-width;
  min-width: 150px;
  cursor: pointer;

  .text {
    z-index: 3;
    position: absolute;
    top: 50%;
    right: 45px;
    -webkit-transform: translateY(-50%);
    transform: translateY(-50%);
  }

  .filter-label-svg {
    height: 100%;
    transform-origin: center;
    position: absolute;
    top: 0;
    right: 0;
    z-index: 0;

    .st1 {
      fill: $filter-label-background-color;
    }

    .st0 {
      fill: $standard-border-color;
    }
  }
}

.filter-separator {
  background-color: $filter-inner-border-color;
  margin: 20px 0px;
}

.filter-active {
  input {
    border: 1px solid $highlight;
  }
}

$broomWidth: 35px;
.filter {
  background-color: $filter-background-color;
  position: relative;
  height: auto;
  min-height: 650px;
  border: 1px solid $standard-border-color;
  width: $filter-width;
  margin-right: $label-width + 15px;
  -webkit-transition: all 300ms ease;
  transition: all 300ms ease;
  z-index: 8;
  font-size: 0.9rem;

  .broom {
    margin-top: -5px;
    position: relative;
    cursor: pointer;
    background-color: lighten($filter-background-color, 10%);
    -webkit-transition: all 300ms ease;
    transition: all 300ms ease;
    height: $broomWidth;
    width: $broomWidth;

    .icon {
      position: absolute;
      top: 50%;
      left: 50%;
      -webkit-transform: translate(-50%, -50%);
      transform: translate(-50%, -50%);
    }

    .hover-text {
      position: absolute;
      background-color: $highlight;
      z-index: 15;
      width: 0;
      overflow: hidden;
      left: $broomWidth;
      height: $broomWidth;
      top: 0px;
      -webkit-transition: all 300ms ease;
      transition: all 300ms ease;
      opacity: 0;

      span {
        display: block;
        position: absolute;
        padding-left: 5px;
        width: 100%;
        min-width: 100px;
        top: 50%;
        left: 50%;
        -webkit-transform: translate(-50%, -50%);
        transform: translate(-50%, -50%);
      }
    }

    &:hover {
      background-color: darken($filter-background-color, 5%);

      .hover-text {
        display: block;
        height: $broomWidth;
        width: 150px;
        opacity: 1;
      }
    }
  }

  &.closed {
    margin-left: - $filter-width;
  }

  h3 {
    text-transform: uppercase;
    padding-bottom: 0.5em;
    border-bottom: 1px solid $filter-inner-border-color;
  }

  .form-text {
    width: 100%;
  }
}

.mini-filter {
  .form-text {
    width: 100%;
  }
}

</style>
